import React, { useEffect, useState } from "react";
// Checklist labels (should match form)
import './horizonTable.css';
import { toast } from "react-toastify";
import SignatureCanvas from "react-signature-canvas";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import onsiteproLogo from '../../assets/logo2 2.png';
import CheckTick from "../../assets/icons/Check-Tick.png";
import AdminTaylorImportModal, { adminIsPrimaryAdmin } from "./AdminTaylorImportModal";
import { ensureFormUuid } from "../../utils/formUuid";
import { buildSharePointFileName, toDdMmYyyy, addedByName } from "../../utils/formPdfHelpers";
import "../forms/themeCheckbox.css";

import { canEditDeleteRecords } from "../../utils/rolePermissions";
const MEWP_THEME = "#8b1634";
const mewpEditLabelStyle = { fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6, display: "block" };
const mewpEditInputStyle = {
  borderRadius: 8,
  border: "1px solid #e2e8f0",
  fontSize: 14,
  padding: "8px 12px",
};

const checkLabels = [
  "Hydraulic oil level in tank",
  "Machine warning lights / audible safety devices",
  "For damage particularly to the arm and work platform",
  "For cracks or deformation particularly to the arm and work platform",
  "Harness anchor points for security",
  "Cylinders, hoses, pipes and valves for damage or hydraulic leaks",
  "Platform for security of all fastenings",
  "Correct function of holding valves",
  "For missing or illegible operational markings",
  "Operation, security, and condition of stabiliser legs",
  "Function of all platform and pedestal controls at low level to confirm correct function and controls are free and return to the neutral position",
  "Operation of emergency lowering controls and valves",
  "Interlocks for correct function",
  "Operation of emergency stop buttons",
  "Manufactures Instruction Manual is available with vehicle and referred to for any additional checks that are required for the particular vehicle",
  "Insulated aerial device (IAD); arms, bucket, and liners for damage to the insulating components",
  "Statutory 6 month, Lifting Operations & Lifting Equipment Regulations 1998 (LOLER) Inspection certificate is available and in date"
];


function MewpChecklistPage() {
  const canEditDelete = canEditDeleteRecords();
  const [checklists, setChecklists] = useState([]);
  const [appUsers, setAppUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [uploadingKey, setUploadingKey] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const isAdminUser = adminIsPrimaryAdmin();
  const [deleteId, setDeleteId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editChecklist, setEditChecklist] = useState(null);
  
  // SharePoint upload modal state
  const [showSharePointUploadModal, setShowSharePointUploadModal] = useState(false);
  const [sharePointUploadRecord, setSharePointUploadRecord] = useState(null);
  const [sharePointPhaseType, setSharePointPhaseType] = useState('phase');
  const [sharePointPhaseNumber, setSharePointPhaseNumber] = useState('15');
  const [sharePointNetwork, setSharePointNetwork] = useState('');
  const [pendingDirectUpload, setPendingDirectUpload] = useState(false);
  useEffect(() => {
    if (pendingDirectUpload && sharePointUploadRecord) {
      setPendingDirectUpload(false);
      // eslint-disable-next-line no-use-before-define
      handleSharePointUpload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDirectUpload, sharePointUploadRecord]);
  const [editSignature, setEditSignature] = useState("");
  const editSigCanvasRef = React.useRef(null);
  // Edit Checklist
  const handleEdit = (checklist) => {
    setEditChecklist({
      ...checklist,
      formUuid: ensureFormUuid(checklist?.formUuid),
      mcqQuestions: checklist.mcqQuestions || { "Fit for use": false, "Defective": false },
      operatorSignature: checklist.operatorSignature || ""
    });
    setEditSignature(checklist.operatorSignature || "");
    setShowEditModal(true);
  };
  const handleEditChange = (field, value) => {
    setEditChecklist(prev => ({ ...prev, [field]: value }));
  };
  const handleEditMcqChange = (status) => {
    setEditChecklist(prev => {
      const newMcqQuestions = {
        "Fit for use": status === "Fit for use",
        "Defective": status === "Defective"
      };
      return {
        ...prev,
        mcqQuestions: newMcqQuestions
      };
    });
  };
  const clearEditSignature = () => {
    if (editSigCanvasRef.current) {
    editSigCanvasRef.current.clear();
    }
    setEditSignature("");
    setEditChecklist(prev => ({ ...prev, operatorSignature: "" }));
  };
  const saveEditSignature = () => {
    if (editSigCanvasRef.current && !editSigCanvasRef.current.isEmpty()) {
      // Get the full canvas instead of trimmed to maintain proper size
      const canvas = editSigCanvasRef.current.getCanvas();
      // Save with high quality
      const sig = canvas.toDataURL("image/png", 1.0);
      setEditSignature(sig);
      setEditChecklist(prev => ({ ...prev, operatorSignature: sig }));
    }
  };
  // Checklist item toggle for edit modal
  const handleEditChecklistToggle = (item) => {
    setEditChecklist(prev => ({
      ...prev,
      checklist: {
        ...prev.checklist,
        [item]: !prev.checklist[item]
      }
    }));
  };

  const handleEditSave = async () => {
    if (!editChecklist.operatorName || !editChecklist.siteAddress || !editChecklist.mewpSerialNumber) {
      toast.error("All fields are required");
      return;
    }
    
    // Debug: Check mcqQuestions before saving
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      // Get user details for saving firstName and lastName
      const user = appUsers.find(u => u._id === editChecklist.userId);
      const addedByFirstName = user ? user.firstName : '';
      const addedByLastName = user ? user.lastName : '';
      
      const res = await fetch(baseUrl + `/api/mewp-checklist/${editChecklist._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({
          operatorName: editChecklist.operatorName,
          siteAddress: editChecklist.siteAddress,
          mewpSerialNumber: editChecklist.mewpSerialNumber,
          notesComments: editChecklist.notesComments,
          checklist: editChecklist.checklist,
          mcqQuestions: editChecklist.mcqQuestions,
          operatorSignature: editChecklist.operatorSignature,
          formUuid: ensureFormUuid(editChecklist.formUuid),
          addedByFirstName,
          addedByLastName
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setChecklists(checklists.map(c => c._id === editChecklist._id ? { ...c, ...editChecklist } : c));
        setShowEditModal(false);
        toast.success("Checklist updated!");
      } else {
        toast.error(data.message || "Failed to update");
      }
    } catch {
      toast.error("Failed to update");
    }
  };

  useEffect(() => {
    async function fetchChecklists() {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
        const token = localStorage.getItem("token");
        const res = await fetch(baseUrl + "/api/mewp-checklist", {
          headers: { "Authorization": `Bearer ${token}` }
        });
        const data = await res.json();
        if (res.ok && data.success && Array.isArray(data.data)) {
          setChecklists(data.data);
        } else {
          setChecklists([]);
        }
      } catch {
        setChecklists([]);
      }
    }
    async function fetchAppUsers() {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
        const token = localStorage.getItem("token");
        const res = await fetch(baseUrl + "/api/app-users", {
          headers: { "Authorization": `Bearer ${token}` }
        });
        const data = await res.json();
        if (res.ok && data.success && Array.isArray(data.data)) {
          setAppUsers(data.data);
        } else {
          setAppUsers([]);
        }
      } catch {
        setAppUsers([]);
      }
    }
    fetchChecklists();
    fetchAppUsers();
  }, []);

  // Improved search: split search string into words, ignore extra spaces, match if all words are present in any field
  const filtered = checklists.filter(c => {
    const fields = [c.operatorName, c.siteAddress, c.mewpSerialNumber].map(val => (val || '').toLowerCase());
    const searchWords = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (searchWords.length === 0) return true;
    return searchWords.every(word => fields.some(field => field.includes(word)));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice().slice((page-1)*pageSize, page*pageSize);
  const formatDateForDisplay = (d) => {
    if (!d) return "—";
    const x = new Date(d);
    return isNaN(x.getTime()) ? d : x.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  
  // Reset page to 1 if current page is beyond available pages
  if (filtered.length === 0 && page > 1) {
    setPage(1);
  }

  // SharePoint Upload Handler
  const handleSharePointUpload = async () => {
    const locationForUpload = (sharePointUploadRecord?.siteAddress || '').trim();

    if (!locationForUpload) {
      toast.error('Site Address is missing for this record.');
      return;
    }

    if (sharePointPhaseType === 'phase' && (!sharePointNetwork || sharePointNetwork.trim() === '')) {
      toast.error('Please select an area (EPN, LPN, or SPN)');
      return;
    }

    const payloadPhaseType = sharePointPhaseType === 'phase' ? 'phase' : 'non-phase';
    const payloadNetwork = sharePointPhaseType === 'phase' ? sharePointNetwork : '';
    const payloadPhaseNumber = sharePointPhaseType === 'phase' ? sharePointPhaseNumber : '';

    setShowSharePointUploadModal(false);

    await generatePDF(sharePointUploadRecord, {
      uploadOnly: true,
      phaseType: payloadPhaseType,
      phaseNumber: payloadPhaseNumber,
      network: payloadNetwork,
      siteName: locationForUpload,
      onSuccess: () => {
        setSharePointUploadRecord(null);
        setSharePointPhaseType('phase');
        setSharePointPhaseNumber('15');
        setSharePointNetwork('');
      },
      onError: () => {}
    });
  };

  // Delete Checklist
  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };
  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      const res = await fetch(baseUrl + `/api/mewp-checklist/${deleteId}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setChecklists(checklists.filter(c => c._id !== deleteId));
        toast.success("Deleted successfully!");
      } else {
        toast.error(data.message || "Failed to delete");
      }
    } catch {
      toast.error("Failed to delete");
    }
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const generatePDF = async (record, options = {}) => {
    const shouldUploadToSharePoint = options.uploadOnly === true;
    const uploadConfig = shouldUploadToSharePoint ? {
      phaseType: options.phaseType || 'non-phase',
      phaseNumber: options.phaseNumber || '',
      network: options.network || '',
      siteName: options.siteName || '',
      onSuccess: options.onSuccess,
      onError: options.onError
    } : null;
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    
    // Modern color scheme
    const primaryColor = [139, 22, 52]; // #8b1634
    const secondaryColor = [233, 207, 214]; // #e9cfd6
    
    // Helper function to draw modern rounded rectangle
    const drawRoundedRect = (x, y, width, height, radius, fillColor, strokeColor, hasShadow = false) => {
      if (hasShadow) {
        doc.setFillColor(0, 0, 0, 0.1);
        doc.roundedRect(x + 1, y + 1, width, height, radius, radius, 'F');
      }
      doc.setFillColor(...fillColor);
      doc.setDrawColor(...strokeColor);
      doc.setLineWidth(0.5);
      doc.roundedRect(x, y, width, height, radius, radius, 'FD');
    };
    
    // ---- HEADER SECTION ----
    const headerHeight = 30;
    drawRoundedRect(0, 0, pageWidth, headerHeight, 0, primaryColor, primaryColor);
    
    // ---- ADD HIGH-RES LOGO ----
    const addLogoToPDF = () => {
      return new Promise((resolve) => {
        const logoImg = new Image();
        logoImg.crossOrigin = 'anonymous';
        logoImg.onload = function () {
          try {
            const logoWidth = 70;
            const aspectRatio = logoImg.height / logoImg.width;
            const logoHeight = logoWidth * aspectRatio;
            const logoX = 10;
            const logoY = (headerHeight - logoHeight) / 2;
            
            const canvas = document.createElement('canvas');
            const scale = 2.5;
            canvas.width = logoImg.width * scale;
            canvas.height = logoImg.height * scale;
            const ctx = canvas.getContext('2d');
            ctx.scale(scale, scale);
            ctx.drawImage(logoImg, 0, 0);
            
            const highResDataUrl = canvas.toDataURL('image/png', 1.0);
            doc.addImage(highResDataUrl, 'PNG', logoX, logoY, logoWidth, logoHeight, '', 'NONE');
          } catch (error) {
            doc.setFillColor(255, 255, 255);
            doc.rect(10, 6, 60, 15, 'F');
            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);
            doc.setTextColor(0, 0, 0);
            doc.text("TAYLOR TECHNOLOGY", 15, 12);
            doc.text("SYSTEMS LIMITED", 15, 17);
          }
          resolve();
        };
        logoImg.onerror = function () {
          doc.setFillColor(255, 255, 255);
          doc.rect(10, 6, 60, 15, 'F');
          doc.setFont("helvetica", "bold");
          doc.setFontSize(10);
          doc.setTextColor(0, 0, 0);
          doc.text("TAYLOR TECHNOLOGY", 15, 12);
          doc.text("SYSTEMS LIMITED", 15, 17);
          resolve();
        };
        logoImg.src = onsiteproLogo;
      });
    };
    
    await addLogoToPDF();
    
    // ---- HEADER TEXT ----
    const titleX = 90;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(255, 255, 255);
    doc.text("MEWP Checklist", titleX, 13);
    
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text("TAYLOR TECHNOLOGY SYSTEMS LIMITED", titleX, 19);
    
    let yPosition = headerHeight + 10;
    
    // ---- Section 1: Basic Information ----
    doc.setFillColor(139, 22, 52);
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    
    doc.rect(10, yPosition, pageWidth - 20, 8, "F");
    doc.text("1", 14, yPosition + 5.5);
    doc.text("Basic Information", 22, yPosition + 5.5);
    yPosition += 8;
    
    autoTable(doc, {
      startY: yPosition,
      columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 70 },
        2: { cellWidth: 35 },
        3: { cellWidth: 35 },
      },
      head: [
        [
          { content: "Operator Name:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
          { content: record.operatorName || "", styles: { halign: "center" } },
          { content: "MEWP Serial No:", styles: { fillColor: [230, 230, 230], fontStyle: "bold", halign: "center" } },
          { content: record.mewpSerialNumber || "", styles: { halign: "center" } },
        ],
      ],
      body: [
        [
          { content: "Site Address:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
          { content: record.siteAddress || "", styles: { halign: "center" } },
          { content: "Notes/Comments:", styles: { fillColor: [230, 230, 230], fontStyle: "bold", halign: "center" } },
          { content: record.notesComments || "", styles: { halign: "center" } },
        ],
      ],
      styles: {
        font: "helvetica",
        fontSize: 9,
        cellPadding: 3,
        overflow: "linebreak",
        valign: "middle",
      },
      theme: "grid",
      tableWidth: pageWidth - 20,
      margin: { left: 10 },
      headStyles: {
        fillColor: [245, 245, 245],
        textColor: [0, 0, 0],
        lineWidth: 0.2,
      },
      bodyStyles: {
        lineWidth: 0.2,
        textColor: [0, 0, 0],
      },
    });
    
    yPosition = doc.lastAutoTable.finalY + 2;
    
    // ---- Section 2: Equipment Status ----
    if (yPosition + 30 > pageHeight - 30) {
      doc.addPage();
      yPosition = 20;
    }
    
    doc.setFillColor(139, 22, 52);
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.rect(10, yPosition, pageWidth - 20, 8, "F");
    doc.text("2", 14, yPosition + 5.5);
    doc.text("Equipment Status", 22, yPosition + 5.5);
    yPosition += 8;
    
    const equipmentStatus = record.mcqQuestions || {};
    const statusText = equipmentStatus['Fit for use'] ? 'Fit for use' : 
                      equipmentStatus['Defective'] ? 'Defective' : 'Not specified';
    
    autoTable(doc, {
      startY: yPosition,
      columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 140 },
      },
      head: [
        [
          { content: "Equipment Status:", styles: { fillColor: [240, 240, 240], fontStyle: "bold" } },
          { content: statusText, styles: { halign: "center" } },
        ],
      ],
      styles: {
        font: "helvetica",
        fontSize: 9,
        cellPadding: 4,
        halign: "left",
        valign: "middle",
      },
      theme: "grid",
      tableWidth: pageWidth - 20,
      margin: { left: 10 },
      headStyles: {
        textColor: [0, 0, 0],
        fillColor: [245, 245, 245],
        lineWidth: 0.2,
      },
      bodyStyles: {
        lineWidth: 0.2,
      },
    });
    
    yPosition = doc.lastAutoTable.finalY + 2;
    
    // ---- Section 3: Checklist Items ----
    if (yPosition + 50 > pageHeight - 30) {
      doc.addPage();
      yPosition = 20;
    }
    
    doc.setFillColor(139, 22, 52);
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.rect(10, yPosition, pageWidth - 20, 8, "F");
    doc.text("3", 14, yPosition + 5.5);
    doc.text("Checklist Items", 22, yPosition + 5.5);
    yPosition += 8;
    
    // Checklist Items Table
    const checklist = record.checklist || {};
    const tableBody = checkLabels.map(label => [
      { content: label || '', styles: { halign: "left", fontSize: 7.5 } },
      { content: '', styles: { halign: "center" } } // Empty content, will be drawn as image
    ]);
    
    autoTable(doc, {
      startY: yPosition,
      head: [
        [
          { content: "Checklist Item", styles: { fillColor: [240, 240, 240], fontStyle: "bold" } },
          { content: "Completed", styles: { fillColor: [240, 240, 240], fontStyle: "bold", halign: "center" } },
        ],
      ],
      body: tableBody,
      didDrawCell: (data) => {
        if (data.section === 'body' && data.column.index === 1) {
          const label = checkLabels[data.row.index];
          if (label && checklist[label] === true) {
            const cell = data.cell;
            const centerX = cell.x + cell.width / 2;
            const centerY = cell.y + cell.height / 2;
            doc.addImage(CheckTick, "PNG", centerX - 2, centerY - 2, 4, 4);
          }
        }
      },
      styles: {
        font: "helvetica",
        fontSize: 8,
        cellPadding: 3,
        halign: "left",
        valign: "middle",
      },
      theme: "grid",
      tableWidth: pageWidth - 20,
      margin: { left: 10 },
      headStyles: {
        textColor: [0, 0, 0],
        fillColor: [245, 245, 245],
        lineWidth: 0.2,
      },
      bodyStyles: {
        lineWidth: 0.2,
      },
    });
    
    yPosition = doc.lastAutoTable.finalY + 2;
    
    // ---- Section 6: Signatories ----
    if (yPosition + 70 > pageHeight - 30) {
      doc.addPage();
      yPosition = 20;
    }
    
    doc.setFillColor(139, 22, 52);
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.rect(10, yPosition, pageWidth - 20, 10, "F");
    doc.text("Section 6 : Signatories", 15, yPosition + 7);
    yPosition += 18;
    
    // Signature box (half width)
    const boxWidth = (pageWidth - 20) / 2;
    const boxHeight = 50;
    doc.setDrawColor(200, 200, 200);
    doc.setFillColor(248, 248, 248);
    doc.roundedRect(10, yPosition, boxWidth, boxHeight, 3, 3, "FD");
    
    // Label
    doc.setTextColor(139, 22, 52);
    doc.setFontSize(12);
    doc.text("Operator Signature", 15, yPosition + 12);
    
    // Signature image
    if (record.operatorSignature) {
      try {
        let sigSrc = record.operatorSignature;
        if (!sigSrc.startsWith('data:image')) {
          sigSrc = 'data:image/png;base64,' + sigSrc;
        }
        
        const sigImg = new Image();
        sigImg.crossOrigin = 'anonymous';
        await new Promise((resolve) => {
          sigImg.onload = () => {
            try {
              const imgWidth = 35;
              const aspectRatio = sigImg.height / sigImg.width;
              const imgHeight = Math.min(35 * aspectRatio, 25);
              const imgX = 15;
              const imgY = yPosition + 15;
              doc.addImage(sigImg, "PNG", imgX, imgY, imgWidth, imgHeight);
            } catch (err) {
              console.error('Error adding signature:', err);
            }
            resolve();
          };
          sigImg.onerror = () => resolve();
          sigImg.src = sigSrc;
        });
      } catch (err) {
        console.error('Signature error:', err);
      }
    }
    
    yPosition += boxHeight + 10;
    
    // ---- Footer Section ----
    const footerY = pageHeight - 25;
    drawRoundedRect(0, footerY, pageWidth, 25, 0, [248, 249, 250], [200, 200, 200]);
    
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`,
      20,
      footerY + 8
    );
    doc.text("Page 1 of 1", pageWidth - 30, footerY + 15);
    
    // Decorative border
    doc.setDrawColor(139, 22, 52);
    doc.setLineWidth(1);
    doc.rect(5, 5, pageWidth - 10, pageHeight - 10);
    
    // Build filename and either save or upload
    const safeName = String(record.operatorName || 'MEWP').replace(/[\\/:"*?<>|]+/g, '-');
    const safeSerial = String(record.mewpSerialNumber || 'Checklist').replace(/[\\/:"*?<>|]+/g, '-');
    
    let fileName;
    if (shouldUploadToSharePoint && uploadConfig) {
      // This form captures no date, so the MEWP serial number stands in as the
      // third segment to keep repeat checks on one site distinguishable.
      fileName = buildSharePointFileName([
        record.operatorName,
        record.siteAddress,
        record.mewpSerialNumber,
      ]);
    } else {
      fileName = `MEWP_${safeName}_${safeSerial}.pdf`;
    }
    
    if (shouldUploadToSharePoint && uploadConfig) {
      try {
        const key = `${record._id || ''}|${record.operatorName || ''}|${record.mewpSerialNumber || ''}`;
        setUploadingKey(key);
        const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
        const dataUri = doc.output('datauristring');
        
        // Prepare SharePoint upload parameters
        const uploadParams = {
          fileName,
          fileBase64: dataUri,
          phaseType: uploadConfig.phaseType,
          phaseNumber: uploadConfig.phaseNumber,
          network: uploadConfig.network,
          siteName: uploadConfig.siteName, uploadContext: "hseq-forms-tablets", uploadSubFolder: "MEWP Checklist"
        };
        
        const res = await fetch(`${apiUrl}/api/sharepoint/upload`, {
          method: 'POST',
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
          body: JSON.stringify(uploadParams)
        });
        if (res.status !== 204 && !res.ok) {
          let msg = 'SharePoint upload failed';
          try {
            const out = await res.json();
            msg = out?.message || msg;
          } catch (_) {}
          throw new Error(msg);
        }
        toast.success("PDF uploaded to SharePoint successfully!");
        if (uploadConfig.onSuccess) {
          uploadConfig.onSuccess();
        }
      } catch (err) {
        toast.error(err?.message || "SharePoint upload failed");
        if (uploadConfig.onError) {
          uploadConfig.onError(err);
        }
      } finally {
        setUploadingKey(null);
      }
    } else {
      doc.save(fileName);
      toast.success("Professional MEWP Checklist PDF generated successfully!");
    }
  };

  return (
    <>
      <div style={{
        background: '#ffffff',
        borderRadius: '20px',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
        padding: '24px',
        marginBottom: '24px'
      }}>
        {/* Header Section */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#1a202c',
            margin: 0
          }}>MEWP Checklist</h2>
          
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            {isAdminUser && (
              <button
                type="button"
                onClick={() => setShowImportModal(true)}
                style={{
                  background: "#8b1634",
                  color: "#fff",
                  border: "none",
                  borderRadius: "10px",
                  padding: "10px 14px",
                  fontWeight: "600",
                  fontSize: "13px",
                  cursor: "pointer",
                }}
              >
                Import File
              </button>
            )}
            <div style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center'
            }}>
              <i className="fa fa-search" style={{
                position: 'absolute',
                left: '12px',
                color: '#8b1634',
                fontSize: '14px'
              }}></i>
              <input 
                type="text" 
                placeholder="Type to Search" 
                value={search} 
                onChange={e=>{ setSearch(e.target.value); setPage(1); }}
                style={{
                  padding: '10px 12px 10px 36px',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  fontSize: '14px',
                  outline: 'none',
                  width: '250px',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#8b1634'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
            </div>
          </div>
        </div>

        {/* Table Container */}
        <div style={{
          overflowX: 'auto',
          borderRadius: '12px',
          border: '1px solid #e2e8f0'
        }}>
          {/* Table Header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1.5fr 1fr 1.5fr 1fr 1fr 1.2fr',
            background: '#f7fafc',
            padding: '12px 16px',
            borderBottom: '1px solid #e2e8f0',
            gap: '16px',
            alignItems: 'center'
          }}>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Operator Name</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Site Address</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>MEWP Serial No</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Notes/Comments</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Added By</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Updated Date</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Actions</div>
          </div>

          {/* Table Body */}
          <div>
            {paged.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                color: '#718096',
                fontSize: '16px'
              }}>No records found.</div>
            ) : paged.map((c, idx) => {
                // First check if firstName and lastName are saved in the record
                // If not, try to find user from appUsers array
                let userName = '-';
                if (c.addedByFirstName && c.addedByLastName) {
                  userName = `${c.addedByFirstName} ${c.addedByLastName}`;
                } else {
                  const user = appUsers.find(u => u._id === c.userId);
                  userName = user ? `${user.firstName} ${user.lastName}` : (c.userId || '-');
                }
                return (
                  <div 
                    key={c._id || idx}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1.5fr 1fr 1.5fr 1fr 1fr 1.2fr',
                      padding: '16px',
                      borderBottom: '1px solid #f1f5f9',
                      gap: '16px',
                      alignItems: 'center',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{c.operatorName || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{c.siteAddress || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{c.mewpSerialNumber || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{c.notesComments || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{userName || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{formatDateForDisplay(c.updatedAt || c.createdAt)}</div>
                    <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                  {canEditDelete && (
                    <button 
                      onClick={()=>handleEdit(c)}
                      title="Edit"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '6px',
                        borderRadius: '6px',
                        transition: 'background-color 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0fdf4'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <i className="fa fa-edit" style={{color:'#22c55e', fontSize: '16px'}}></i>
                    </button>
                  )}
                  <button 
                    onClick={()=>generatePDF(c)}
                    title="Generate PDF"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '6px',
                      borderRadius: '6px',
                      transition: 'background-color 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <i className="fa fa-file-pdf-o" style={{color:'#dc2626', fontSize: '16px'}}></i>
                  </button>
                  {(() => { 
                    const k = `${c._id || ''}|${c.operatorName || ''}|${c.mewpSerialNumber || ''}`; 
                    const busy = uploadingKey === k; 
                    return (
                      <button 
                        onClick={()=>{
                          if (!busy) {
                            setSharePointUploadRecord(c);
                            setSharePointPhaseType('phase');
                            setSharePointPhaseNumber('15');
                            setSharePointNetwork('');
                            setSharePointPhaseType("non-phase"); setPendingDirectUpload(true);
                          }
                        }}
                        disabled={busy}
                        title={busy ? "Uploading..." : "Upload to SharePoint"}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: busy ? 'not-allowed' : 'pointer',
                          padding: '6px',
                          borderRadius: '6px',
                          transition: 'background-color 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: busy ? 0.6 : 1
                        }}
                        onMouseEnter={(e) => !busy && (e.currentTarget.style.backgroundColor = '#f0fdf4')}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <i className={busy ? "fa fa-spinner fa-spin" : "fa fa-upload"} style={{color:'#22c55e', fontSize: '16px'}}></i>
                      </button>
                    ); 
                  })()}
                  {canEditDelete && (
                    <button 
                      onClick={()=>handleDelete(c._id)}
                      title="Delete"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '6px',
                        borderRadius: '6px',
                        transition: 'background-color 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <i className="fa fa-trash" style={{color:'#e53e3e', fontSize: '16px'}}></i>
                    </button>
                  )}
                </div>
              </div>
                );
            })}
          </div>
        </div>

        {/* Pagination */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          marginTop: '18px',
          gap: '12px'
        }}>
          <button 
            className="horizon-table-btn" 
            disabled={page===1 || filtered.length === 0} 
            onClick={()=>setPage(page-1)}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              background: (page===1 || filtered.length === 0) ? '#f7fafc' : '#ffffff',
              color: (page===1 || filtered.length === 0) ? '#a0aec0' : '#8b1634',
              cursor: (page===1 || filtered.length === 0) ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >Prev</button>
          <span style={{
            fontWeight: '700',
            color: '#8b1634',
            fontSize: '16px'
          }}>{page} / {totalPages}</span>
          <button 
            className="horizon-table-btn" 
            disabled={page===totalPages || filtered.length === 0} 
            onClick={()=>setPage(page+1)}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              background: (page===totalPages || filtered.length === 0) ? '#f7fafc' : '#ffffff',
              color: (page===totalPages || filtered.length === 0) ? '#a0aec0' : '#8b1634',
              cursor: (page===totalPages || filtered.length === 0) ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >Next</button>
        </div>

      {/* Edit Modal */}
      {showEditModal && editChecklist && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.45)" }} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{ maxWidth: "1100px", margin: "0 auto", border: "none", borderRadius: 14, overflow: "hidden", boxShadow: "0 12px 40px rgba(0,0,0,0.18)" }}>
              <div className="modal-header" style={{ borderBottom: "none", padding: "16px 20px", background: `linear-gradient(135deg, ${MEWP_THEME} 0%, #a01a3f 100%)` }}>
                <h5 className="modal-title" style={{ color: "#fff", fontWeight: 600, fontSize: 18, margin: 0 }}>Edit MEWP Checklist</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowEditModal(false)} />
              </div>
              <div className="modal-body" style={{ padding: "20px", background: "#fff", maxHeight: "75vh", overflowY: "auto" }}>
                <div className="row g-3 mb-1">
                  <div className="col-12 col-md-4">
                    <label style={mewpEditLabelStyle}>Site Address</label>
                    <input type="text" className="form-control" style={mewpEditInputStyle} value={editChecklist.siteAddress} onChange={(e) => handleEditChange("siteAddress", e.target.value)} />
                  </div>
                  <div className="col-12 col-md-4">
                    <label style={mewpEditLabelStyle}>MEWP Serial No</label>
                    <input type="text" className="form-control" style={mewpEditInputStyle} value={editChecklist.mewpSerialNumber} onChange={(e) => handleEditChange("mewpSerialNumber", e.target.value)} />
                  </div>
                  <div className="col-12 col-md-4">
                    <label style={mewpEditLabelStyle}>Notes/Comments</label>
                    <input type="text" className="form-control" style={mewpEditInputStyle} value={editChecklist.notesComments} onChange={(e) => handleEditChange("notesComments", e.target.value)} />
                  </div>
                </div>
                <div className="row g-3 mt-1">
                  <div className="col-12 col-md-4">
                    <label style={mewpEditLabelStyle}>Equipment Status</label>
                    <div className="d-flex flex-wrap gap-4 pt-1" key={editChecklist.mcqQuestions ? JSON.stringify(editChecklist.mcqQuestions) : "default"}>
                      <label className="d-flex align-items-center gap-2 mb-0" style={{ cursor: "pointer", fontWeight: 500, color: "#374151" }}>
                        <input type="radio" name="equipmentStatusEdit" value="Fit for use" checked={editChecklist.mcqQuestions?.["Fit for use"] === true} onChange={() => handleEditMcqChange("Fit for use")} className="form-check-input theme-checkbox" />
                        Fit for use
                      </label>
                      <label className="d-flex align-items-center gap-2 mb-0" style={{ cursor: "pointer", fontWeight: 500, color: "#374151" }}>
                        <input type="radio" name="equipmentStatusEdit" value="Defective" checked={editChecklist.mcqQuestions?.["Defective"] === true} onChange={() => handleEditMcqChange("Defective")} className="form-check-input theme-checkbox" />
                        Defective
                      </label>
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <label style={mewpEditLabelStyle}>Operator Name</label>
                    <input type="text" className="form-control" style={mewpEditInputStyle} value={editChecklist.operatorName} onChange={(e) => handleEditChange("operatorName", e.target.value)} />
                  </div>
                  <div className="col-12 col-md-4">
                    <label style={mewpEditLabelStyle}>Operator Signature</label>
                    <div style={{
                      width: "100%",
                      border: `2px solid ${MEWP_THEME}`,
                      borderRadius: 12,
                      background: "#f8f4f5",
                      padding: "12px",
                      textAlign: "center",
                      minHeight: 120,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                    }}>
                      {editChecklist.operatorSignature ? (
                        <img 
                          src={editChecklist.operatorSignature.startsWith('data:image') 
                            ? editChecklist.operatorSignature 
                            : `data:image/png;base64,${editChecklist.operatorSignature}`}
                          alt="Operator Signature" 
                          style={{
                            maxWidth: '100%',
                            maxHeight: '100px',
                            objectFit: 'contain',
                            border: '1px solid #ddd',
                            borderRadius: '8px',
                            padding: '4px',
                            backgroundColor: 'white'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <span style={{ color: "#9ca3af", fontSize: 14 }}>No signature</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mt-4">
                  <label style={{ ...mewpEditLabelStyle, color: MEWP_THEME, fontSize: 14, marginBottom: 10 }}>Checklist Items</label>
                  <div style={{ maxHeight: 280, overflowY: "auto", border: "1px solid #e8e8e8", borderRadius: 10, background: "#fafafa" }}>
                    {checkLabels.map((item, idx) => (
                      <label
                        key={item + idx}
                        className="d-flex align-items-start gap-3"
                        style={{
                          cursor: "pointer",
                          padding: "10px 14px",
                          borderBottom: idx !== checkLabels.length - 1 ? "1px solid #ececec" : "none",
                          transition: "background 0.15s",
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = "#f5f0f2"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                      >
                        <input
                          className="form-check-input theme-checkbox mt-1 flex-shrink-0"
                          type="checkbox"
                          checked={!!(editChecklist.checklist && editChecklist.checklist[item])}
                          onChange={() => handleEditChecklistToggle(item)}
                        />
                        <span style={{ flex: 1, fontSize: 14, lineHeight: 1.45, color: "#374151" }}>{item}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={{ borderTop: "1px solid #eee", padding: "14px 20px", background: "#fafafa", gap: 10 }}>
                <button type="button" className="btn" style={{ background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 8, padding: "8px 20px", fontWeight: 600, minWidth: 96 }} onClick={() => setShowEditModal(false)}>Cancel</button>
                <button type="button" className="btn" style={{ background: MEWP_THEME, color: "#fff", fontWeight: 600, borderRadius: 8, padding: "8px 24px", border: "none" }} onClick={handleEditSave}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SharePoint Upload Modal */}
      {showSharePointUploadModal && sharePointUploadRecord && (
        <div className="modal fade show" style={{display:'block', background:'rgba(0,0,0,0.3)'}} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{maxWidth:'600px', margin:'0 auto'}}>
              <div className="modal-header">
                <h5 className="modal-title">Upload to SharePoint</h5>
                <button type="button" className="btn-close" onClick={()=>{
                  setShowSharePointUploadModal(false);
                  setSharePointUploadRecord(null);
                  setSharePointPhaseType('phase');
                  setSharePointPhaseNumber('15');
                  setSharePointNetwork('');
                }}></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="mb-3 col-12">
                    <label className="form-label fw-semibold">Type <span style={{color:'red'}}>*</span></label>
                    <select 
                      className="form-select" 
                      value={sharePointPhaseType}
                      onChange={(e) => {
                        setSharePointPhaseType(e.target.value);
                        if (e.target.value === 'non-phase') {
                          setSharePointNetwork('');
                        }
                      }}
                    >
                      <option value="phase">Phase</option>
                      <option value="non-phase">Non Phase</option>
                    </select>
                  </div>
                  
                  {sharePointPhaseType === 'phase' && (
                    <>
                      <div className="mb-3 col-md-6">
                        <label className="form-label fw-semibold">Phase Number <span style={{color:'red'}}>*</span></label>
                        <select 
                          className="form-select" 
                          value={sharePointPhaseNumber}
                          onChange={(e) => {
                            setSharePointPhaseNumber(e.target.value);
                          }}
                        >
                          <option value="15">Phase 15 (2025)</option>
                          <option value="16">Phase 16 (2026)</option>
                        </select>
                      </div>
                      <div className="mb-3 col-md-6">
                        <label className="form-label fw-semibold">Area <span style={{color:'red'}}>*</span></label>
                        <select 
                          className="form-select" 
                          value={sharePointNetwork}
                          onChange={(e) => {
                            setSharePointNetwork(e.target.value);
                          }}
                        >
                          <option value="">Select Area</option>
                          <option value="EPN">EPN</option>
                          <option value="LPN">LPN</option>
                          <option value="SPN">SPN</option>
                        </select>
                      </div>
                    </>
                  )}
                  
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={()=>{
                    setShowSharePointUploadModal(false);
                    setSharePointUploadRecord(null);
                    setSharePointPhaseType('phase');
                    setSharePointPhaseNumber('15');
                    setSharePointNetwork('');
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn" 
                  style={{background:'#8b1634', color:'#fff', fontWeight:600}}
                  onClick={handleSharePointUpload}
                >
                  Upload to SharePoint
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AdminTaylorImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Upload MEWP Checklist File"
        importPath="mewp-checklist/import-form"
        isAdminUser={isAdminUser}
        onImported={({ insertedRow }) => {
          if (insertedRow && insertedRow._id) {
            setChecklists(prev => [insertedRow, ...prev]);
            setPage(1);
          }
        }}
      />

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="modal fade show" style={{display:'block', background:'rgba(0,0,0,0.3)'}} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{maxWidth:'1200px', margin:'0 auto'}}>
              <div className="modal-header">
                <h5 className="modal-title">Delete MEWP Checklist</h5>
                <button type="button" className="btn-close" onClick={()=>setShowDeleteModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this record?</p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={()=>setShowDeleteModal(false)}>Cancel</button>
                <button className="btn btn-danger" onClick={confirmDelete}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}

export default MewpChecklistPage;
